const TOKEN_KEY = 'artflow_access_token';

export interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Токен живёт в памяти, а localStorage — лишь способ пережить перезагрузку.
 * Во встроенном браузере Telegram хранилище бывает недоступно или не переживает
 * сессию: единственный экземпляр в localStorage означал бы, что сразу после
 * входа каждый запрос уходит без заголовка и получает «Требуется Bearer-токен».
 */
let accessToken: string | null = read();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;

  // Хранилище — приятное дополнение: без него приложение работает до перезагрузки.
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Приватный режим или заблокированное хранилище: молчим, токен уже в памяти.
  }
}

function read(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Мобильная сеть и перезапуск сервера дают запросы, которые не отвечают
 * никогда. Без ограничения кнопка навсегда оставалась в состоянии «Сохраняем…»,
 * поэтому запрос обрывается сам и превращается в обычную ошибку.
 */
const REQUEST_TIMEOUT_MS = 20_000;

export async function api<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: unknown;
    auth?: boolean;
  } = {},
): Promise<T> {
  const token = getAccessToken();
  let response: Response;

  try {
    response = await fetch(`/v1${path}`, {
      method: options.method ?? 'GET',
      headers: {
        accept: 'application/json',
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(options.auth === false || !token ? {} : { authorization: `Bearer ${token}` }),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (failure) {
    const timedOut = failure instanceof DOMException && failure.name === 'TimeoutError';

    throw new ApiError(
      0,
      timedOut ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR',
      timedOut
        ? 'Сервер не ответил. Проверьте связь и попробуйте ещё раз'
        : 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз',
    );
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const error = payload as ApiErrorPayload | null;
    throw new ApiError(
      response.status,
      error?.error?.code ?? 'REQUEST_FAILED',
      error?.error?.message ?? 'Не удалось выполнить запрос',
      error?.error?.details,
    );
  }

  return payload as T;
}
